import React, { useState } from 'react';
import {
  Eye, EyeOff, Globe2,
  PhoneCall
} from 'lucide-react';
import { Link, useNavigate, } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import GoogleAuthBlock from './GoogleAuthBlock';
import PublicNavbar from '../PublicNavbar';

const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!formData.email) {
        setError('Email is required');
        setLoading(false);
        return;
      }

      await login(formData);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f3f4f6] flex flex-col items-center justify-center font-sans">
      <PublicNavbar />
      <div className="mt-9 max-w-7xl w-full grid grid-cols-1 lg:grid-cols-2 gap-10 p-4 md:p-10 items-center">

        {/* Left Side: Branding and Illustration */}
        <div className="hidden lg:flex flex-col h-full justify-between">
          {/* <div className="mb-12">
            <img src='./images/logoAuth.png' alt='logo' className='w-40' />
          </div> */}

          {/* Illustration Container */}
          <div className="relative flex-1 flex items-center justify-center">
            <img
              src="./images/authImage.png"
              alt="Logistics"
              className="w-full max-w-xl object-contain"
            />
          </div>
        </div>

        {/* Right Side: Login Form */}
        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-slate-100 max-w-xl mx-auto lg:ml-auto w-full">
          <h2 className="text-2xl font-bold text-[#1a2b4b] mb-2">Welcome Back</h2>
          <div className="h-1 w-full bg-slate-100 mb-8 rounded-full"></div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Email Input */}
            <div>
              <label className="block text-sm font-bold text-[#1a2b4b] mb-2">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="rosan@gmail.com"
                className="w-full bg-slate-100 border-none rounded-xl p-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                required
              />
            </div>

            {/* Password Input */}
            <div>
              <div className="flex justify-between items-center mb-2 gap-2">
                <label className="text-sm font-bold text-[#1a2b4b]">Password</label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-blue-600 font-semibold hover:underline shrink-0"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••"
                  className="w-full bg-slate-100 border-none rounded-xl p-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all pr-12"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1a2b4b] text-white font-bold py-4 rounded-xl hover:bg-[#253a63] transition-colors shadow-lg shadow-blue-900/10 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>

          <GoogleAuthBlock
            variant="login"
            setError={setError}
            setLoading={setLoading}
            onCredential={async (idToken) => {
              await loginWithGoogle(idToken);
              navigate('/dashboard');
            }}
          />

          {/* Signup Link */}
          <p className="text-center mt-6 text-sm text-slate-600">
            Don't have an account?{' '}
            <Link to="/signup" className="text-blue-600 font-bold hover:underline">
              Sign Up
            </Link>
          </p>
        </div>

      </div>
      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-16 px-4 sm:px-6 lg:px-8 border-t-4 border-indigo-600">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          <div className="col-span-1 md:col-span-1">
            <div className="cursor-pointer mb-6" onClick={() => navigate('/')}>
              <img src="/images/logo.png" alt="Flywell Logistics" className="h-8 md:h-10 brightness-0 invert opacity-90" />
            </div>
            <p className="text-sm leading-relaxed mb-6">
              Flywell is building an all-in-one tech & service stack for the entire eCommerce value chain.
            </p>
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center hover:bg-indigo-600 cursor-pointer transition-colors">
                <div className="w-4 h-4 rounded-sm bg-slate-400 hover:bg-white transition-colors" />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-white font-bold mb-6 tracking-wide uppercase text-sm">Services</h3>
            <ul className="space-y-4 text-sm font-medium">
              <li><button onClick={() => navigate('/')} className="hover:text-indigo-400 transition-colors">Domestic Shipping</button></li>
              <li><button onClick={() => navigate('/')} className="hover:text-indigo-400 transition-colors">B2B Cargo Shipping</button></li>
              <li><button onClick={() => navigate('/')} className="hover:text-indigo-400 transition-colors">Warehouse & Fulfillment</button></li>
            </ul>
          </div>

          <div>
            <h3 className="text-white font-bold mb-6 tracking-wide uppercase text-sm">Features</h3>
            <ul className="space-y-4 text-sm font-medium">
              <li><button onClick={() => navigate('/')} className="hover:text-indigo-400 transition-colors">Branded Tracking Page</button></li>
              <li><button onClick={() => navigate('/')} className="hover:text-indigo-400 transition-colors">NDR Management</button></li>
              <li><button onClick={() => navigate('/')} className="hover:text-indigo-400 transition-colors">Early Payouts</button></li>
              <li><button onClick={() => navigate('/')} className="hover:text-indigo-400 transition-colors">API Integration</button></li>
            </ul>
          </div>

          <div>
            <h3 className="text-white font-bold mb-6 tracking-wide uppercase text-sm">Contact Us</h3>
            <ul className="space-y-4 text-sm font-medium">
              <li className="flex items-start gap-3">
                <PhoneCall className="w-5 h-5 text-indigo-500 shrink-0" />
                <span>+918826885557</span>
              </li>
              <li className="flex items-start gap-3">
                <Globe2 className="w-5 h-5 text-indigo-500 shrink-0" />
                <span>support@flywelllogistics.com</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm font-medium">
          <p>&copy; {new Date().getFullYear()} Flywell Logistics. All rights reserved.</p>
          <div className="flex gap-6">
            <button className="hover:text-white transition-colors">Privacy Policy</button>
            <button className="hover:text-white transition-colors">Terms of Service</button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LoginPage;
